import { PERMISSIONS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import { RoleFields } from '../forms/role-fields.js';
import { useCreateRole, useUpdateRole } from '../api/mutations.js';
import { useRoleDetail } from '../api/queries.js';
import { type RoleDetail, type RoleFormInput, roleFormInput } from '../api/schemas.js';

/**
 * Creating and editing a role.
 *
 * One component for both, because they are the same fields under the same
 * validation; only the defaults, the title and the mutation differ.
 */

const CREATE_DEFAULTS: RoleFormInput = {
  name: '',
  description: '',
  permissions: [],
};

/** Null columns become empty strings: a controlled input cannot take null. */
function defaultsFrom(role: RoleDetail): RoleFormInput {
  return {
    name: role.name,
    description: role.description ?? '',
    permissions: [...role.permissions],
  };
}

export function RoleFormPage(): React.JSX.Element {
  const roleId = useParams().roleId;
  const isEdit = roleId !== undefined;

  return (
    <PermissionGuard permission={PERMISSIONS.ROLE_WRITE} fallback={<NotPermitted />}>
      {isEdit ? <EditRole roleId={roleId} /> : <CreateRole />}
    </PermissionGuard>
  );
}

function CreateRole(): React.JSX.Element {
  const navigate = useNavigate();
  const create = useCreateRole();

  const form = useAppForm<RoleFormInput>({
    schema: roleFormInput,
    defaultValues: CREATE_DEFAULTS,
    successMessage: 'Role created',
    onSubmit: async (values) => {
      const created = await create.mutateAsync(values);
      void navigate(`/roles/${created.id}`);
    },
  });

  return (
    <FormPage
      title="New role"
      description="Create a role and choose the permissions it grants."
      backTo="/roles"
      backLabel="Roles"
      submitLabel="Create role"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <RoleFields register={form.register} errors={form.formState.errors} control={form.control} />
    </FormPage>
  );
}

function EditRole({ roleId }: { readonly roleId: string }): React.JSX.Element {
  const navigate = useNavigate();
  const update = useUpdateRole();
  const query = useRoleDetail(roleId);

  if (query.isPending) return <LoadingState label="Loading role" />;
  if (query.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? ''}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <EditRoleForm
      role={query.data}
      onSaved={() => {
        void navigate(`/roles/${roleId}`);
      }}
      save={async (values) => {
        await update.mutateAsync({ roleId, values });
      }}
    />
  );
}

function EditRoleForm({
  role,
  save,
  onSaved,
}: {
  readonly role: RoleDetail;
  readonly save: (values: RoleFormInput) => Promise<void>;
  readonly onSaved: () => void;
}): React.JSX.Element {
  const form = useAppForm<RoleFormInput>({
    schema: roleFormInput,
    defaultValues: defaultsFrom(role),
    successMessage: 'Role updated',
    onSubmit: async (values) => {
      await save(values);
      onSaved();
    },
  });

  return (
    <FormPage
      title={`Edit ${role.name}`}
      description="Changes take effect immediately for every user who holds this role."
      backTo="/roles"
      backLabel="Roles"
      submitLabel="Save changes"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <RoleFields register={form.register} errors={form.formState.errors} control={form.control} />
    </FormPage>
  );
}
